import imageCompression from "browser-image-compression";

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, onProgress = () => {}) => {
  const options = {
    maxSizeMB: 1, // Max file size 1MB
    maxWidthOrHeight: 1920, // Max dimension 1920px (Full HD)
    useWebWorker: true, // Use web worker for better performance
    fileType: file.type, // Preserve original file type
    onProgress: (progress) => {
      // Map compression progress to 0-30% range
      onProgress(Math.round(progress * 0.3));
    },
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Image compression failed:", error);
    // If compression fails, return original file
    return file;
  }
};

/**
 * Validate image file before upload
 * @param {File} file - The file to validate
 * @throws {Error} - Throws error if validation fails
 */
export const validateImage = (file) => {
  const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
  // The help center caps images at 5MB (PDFs get 10MB). This runs on the original file,
  // before compression, so a slightly larger source can still end up well under the cap —
  // but claiming 10MB here only moves the rejection to the server.
  const maxSize = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    throw new Error(
      "Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.",
    );
  }

  if (file.size > maxSize) {
    throw new Error("File size exceeds the 5MB limit for images.");
  }

  return true;
};

/**
 * Convert base64 string to File object
 * @param {string} base64String - Base64 encoded image string
 * @param {string} filename - Desired filename
 * @returns {File} - File object
 */
export const base64ToFile = (base64String, filename = "image.png") => {
  const arr = base64String.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};
